/**
 * HeroChoiceTiles
 * ---------------
 * Zwei permanent sichtbare Auswahl-Kacheln direkt neben dem Hero-CTA-
 * Block. Ersetzt seit der Redesign-Runde das automatische Welcome-
 * Choice-Popup — Nutzer sieht die Optionen jetzt sofort, ohne dass ein
 * Modal geöffnet werden muss.
 *
 * Layout: vertikal gestacked innerhalb jeder Kachel (Icon → Titel →
 * Subline → CTA-Button), damit auch längere CTAs wie „UNVERBINDLICH
 * SICHERN →" nicht mit dem Titel kollidieren.
 *
 * Klick-Verhalten (Analytics-kompatibel):
 *   - `trackInteraction("welcome_choice", { choice: "referenz", source: "hero_tile" })`
 *   - `trackInteraction("welcome_choice", { choice: "rabatt",   source: "hero_tile" })`
 *   → Trichter-Widget in /admin/analytics zählt die Klicks weiterhin
 *     korrekt in die vorhandenen Buckets.
 */
import { Gift, Percent, ArrowRight } from "lucide-react";
import type { HeroChoiceTilesConfig } from "@/lib/useAktionsSeite";
import { trackInteraction } from "@/lib/analytics";

type Props = {
    cfg: HeroChoiceTilesConfig;
    onChooseReferenz: () => void;
    onChooseRabatt: () => void;
};

type TileVariant = "gold" | "red";

function Tile({
    variant, icon, title, subline, cta, onClick, testId,
}: {
    variant: TileVariant;
    icon: React.ReactNode;
    title: string;
    subline: string;
    cta: string;
    onClick: () => void;
    testId: string;
}) {
    const isGold = variant === "gold";
    const bg = isGold
        ? "linear-gradient(180deg, #FBF3DE 0%, #F1D48A 100%)"
        : "linear-gradient(180deg, #FFF5F5 0%, #FEE2E2 100%)";
    const borderColor = isGold ? "#A78150" : "#C53030";
    const shadowRest = isGold
        ? "0 6px 20px -8px rgba(167, 129, 80, 0.55)"
        : "0 6px 20px -8px rgba(197, 48, 48, 0.55)";
    const shadowHover = isGold
        ? "0 12px 28px -8px rgba(167, 129, 80, 0.75), 0 0 0 3px rgba(212, 168, 85, 0.25)"
        : "0 12px 28px -8px rgba(197, 48, 48, 0.7), 0 0 0 3px rgba(197, 48, 48, 0.2)";
    const iconBg = isGold ? "rgba(15, 12, 8, 0.85)" : "#C53030";
    const iconBorder = isGold ? "#A78150" : "#7F1D1D";
    const iconColor = isGold ? "#F1D48A" : "#FFF5F5";
    const titleColor = isGold ? "#0F1830" : "#7F1D1D";
    const sublineColor = isGold ? "#6E5220" : "#991B1B";
    const ctaColor = isGold ? "#0F1830" : "#7F1D1D";
    const ctaBg = isGold ? "rgba(255, 255, 255, 0.35)" : "rgba(255, 255, 255, 0.45)";

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="group flex flex-col items-start gap-2 md:min-w-[240px] md:max-w-[280px] w-full text-left rounded-lg transition-all"
            style={{
                background: bg,
                border: `2px solid ${borderColor}`,
                padding: "14px 16px",
                boxShadow: shadowRest,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = shadowHover;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = shadowRest;
            }}
        >
            {/* Zeile 1: Icon + Titelblock (Titel + Subline). Beides links
             *  ausgerichtet, gestacked untereinander. */}
            <div className="flex items-start gap-3 w-full">
                <span
                    aria-hidden
                    className="inline-flex items-center justify-center rounded-full shrink-0"
                    style={{
                        width: 36, height: 36,
                        background: iconBg,
                        border: `1.5px solid ${iconBorder}`,
                        color: iconColor,
                    }}
                >
                    {icon}
                </span>
                <span className="flex-1 min-w-0">
                    <span
                        className="block font-anton uppercase leading-tight text-base md:text-lg break-words"
                        style={{ color: titleColor, letterSpacing: "0.02em" }}
                    >
                        {title}
                    </span>
                    <span
                        className="block italic text-xs mt-0.5"
                        style={{
                            color: sublineColor,
                            fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
                        }}
                    >
                        {subline}
                    </span>
                </span>
            </div>
            {/* Zeile 2: eigener CTA-Chip unten, volle Breite. Kollidiert
             *  nicht mehr mit dem Titel, weil er in einer eigenen Zeile
             *  liegt. */}
            <span
                className="inline-flex items-center justify-center gap-1.5 w-full mt-1 px-3 py-1.5 rounded-md text-xs md:text-sm font-semibold uppercase tracking-wider transition-transform group-hover:translate-x-0.5"
                style={{
                    color: ctaColor,
                    background: ctaBg,
                    border: `1px solid ${borderColor}`,
                }}
            >
                {cta.replace(/\s*→\s*$/, "")}
                <ArrowRight size={14} strokeWidth={2.4} />
            </span>
        </button>
    );
}

export default function HeroChoiceTiles({ cfg, onChooseReferenz, onChooseRabatt }: Props) {
    if (!cfg.enabled) return null;

    const handle = (choice: "referenz" | "rabatt") => {
        try {
            trackInteraction("welcome_choice", { choice, source: "hero_tile" });
        } catch { /* nicht kritisch */ }
        if (choice === "referenz") onChooseReferenz();
        else onChooseRabatt();
    };

    return (
        <div
            className="flex flex-col md:flex-row gap-3 w-full md:w-auto"
            data-testid="hero-choice-tiles"
        >
            <Tile
                variant="gold"
                icon={<Gift size={16} strokeWidth={2.2} />}
                title={cfg.card_referenz_title}
                subline={cfg.card_referenz_subline}
                cta={cfg.card_referenz_cta}
                onClick={() => handle("referenz")}
                testId="hero-choice-tile-referenz"
            />
            <Tile
                variant="red"
                icon={<Percent size={16} strokeWidth={2.6} />}
                title={cfg.card_rabatt_title}
                subline={cfg.card_rabatt_subline}
                cta={cfg.card_rabatt_cta}
                onClick={() => handle("rabatt")}
                testId="hero-choice-tile-rabatt"
            />
        </div>
    );
}
